/**
 * Ring PRO ring variant map. 4 colors (gold, silver, black, titanium) × sizes × engraved/not.
 * Variant IDs and URLs are sourced from ringProVariantIds.ts for easy maintenance.
 */
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  isRingProSkuDisabled,
  RingProColors,
  RingProVariantType,
  RingSizes,
  RingType,
} from './constants';
import { RING_PRO_VARIANTS_DATA_EXPANDED } from './ringProVariantIds';

/** Single variant GID used for all Ring Pro rings in non-production (all sizes, regions, colors). */
const RING_PRO_DEV_VARIANT_GID = 'gid://shopify/ProductVariant/55503290794057';

const SIZES = [
  RingSizes.FIVE,
  RingSizes.SIX,
  RingSizes.SEVEN,
  RingSizes.EIGHT,
  RingSizes.NINE,
  RingSizes.TEN,
  RingSizes.ELEVEN,
  RingSizes.TWELVE,
  RingSizes.THIRTEEN,
  RingSizes.FOURTEEN,
  RingSizes.OPEN,
].filter((size) => !isRingProSkuDisabled({ size }));

function makeVariant(
  color: RingProColors,
  size: RingSizes,
  engraving: RingType,
): RingProVariantType {
  const sizeKey = size === RingSizes.OPEN ? 99 : (size as number);
  const engKey = engraving === RingType.ENGRAVED ? 'E' : 'N';
  const prefix = `RP${color.charAt(0)}`;

  const data =
    RING_PRO_VARIANTS_DATA_EXPANDED[color]?.[sizeKey]?.[engKey] ?? {};

  const variantId: Partial<Record<ShopifyStore, string>> = {};
  const checkoutUrl: Partial<Record<ShopifyStore, string>> = {};

  const useDevVariant = process.env.NEXT_PUBLIC_ENVIRONMENT !== 'PRODUCTION';

  for (const [store, entry] of Object.entries(data)) {
    if (entry) {
      variantId[store as ShopifyStore] = useDevVariant
        ? RING_PRO_DEV_VARIANT_GID
        : entry.id;
      checkoutUrl[store as ShopifyStore] = entry.url;
    }
  }

  return {
    SKU: `${prefix}${sizeKey}${engKey}`,
    variantId,
    checkoutUrl,
  };
}

function buildColorMap(
  color: RingProColors,
): Partial<Record<RingSizes, Record<RingType, RingProVariantType>>> {
  const map: Partial<Record<RingSizes, Record<RingType, RingProVariantType>>> =
    {};
  for (const size of SIZES) {
    map[size] = {
      [RingType.ENGRAVED]: makeVariant(color, size, RingType.ENGRAVED),
      [RingType.NOT_ENGRAVED]: makeVariant(color, size, RingType.NOT_ENGRAVED),
    };
  }
  return map;
}

export const RingProVariants: Record<
  RingProColors,
  Partial<Record<RingSizes, Record<RingType, RingProVariantType>>>
> = {
  [RingProColors.GOLD]: buildColorMap(RingProColors.GOLD),
  [RingProColors.SILVER]: buildColorMap(RingProColors.SILVER),
  [RingProColors.BLACK]: buildColorMap(RingProColors.BLACK),
  [RingProColors.TITANIUM]: buildColorMap(RingProColors.TITANIUM),
};
